import { useState, useEffect, useRef, useCallback } from "react";
import { GetServerSideProps } from "next";
import Head from "next/head";
import { NextSeo } from "next-seo";
import { motion, AnimatePresence } from "framer-motion";
import { Input, InputRef } from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import { ProductItem } from "@/components";
import { Product } from "@/types";
import { sanityFetch } from "@/utils/sanityUtil";
import { pageSeo } from "@/utils/seoContent";
import { productsQuery, categoriesQuery } from "@/utils/sanityQueries";

interface ShopPageProps {
  products: Product[];
  categories: Array<{ title: string; slug: { current: string } }>;
  selectedCategory?: string;
}

export default function ShopPage({
  products,
  categories,
  selectedCategory,
}: ShopPageProps) {
  const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
  const [activeCategory, setActiveCategory] = useState<string>(
    selectedCategory || "all"
  );
  const [sortBy, setSortBy] = useState<string>("newest");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [isSearchSticky, setIsSearchSticky] = useState<boolean>(false);
  const [isScrollingToProducts, setIsScrollingToProducts] =
    useState<boolean>(false);

  // Refs for scroll management
  const heroSearchRef = useRef<HTMLDivElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const stickySearchRef = useRef<HTMLDivElement>(null);
  const stickySearchInputRef = useRef<InputRef>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollStopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let filtered = [...products];

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (product) =>
          product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.description
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          product.productCategory?.title
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (activeCategory !== "all") {
      filtered = filtered.filter(
        (product) => product.productCategory?.slug?.current === activeCategory
      );
    }

    // Sort products
    switch (sortBy) {
      case "price-low":
        filtered.sort((a, b) => {
          const priceA = a.variants[0]?.price || 0;
          const priceB = b.variants[0]?.price || 0;
          return priceA - priceB;
        });
        break;
      case "price-high":
        filtered.sort((a, b) => {
          const priceA = a.variants[0]?.price || 0;
          const priceB = b.variants[0]?.price || 0;
          return priceB - priceA;
        });
        break;
      case "name":
        filtered.sort((a, b) => a.title.localeCompare(b.title));
        break;
      default: // newest
        filtered.sort(
          (a, b) =>
            new Date(b._createdAt).getTime() - new Date(a._createdAt).getTime()
        );
    }

    setFilteredProducts(filtered);
  }, [products, activeCategory, sortBy, searchQuery]);

  // Scroll detection for sticky search with immediate appearance and auto-focus
  useEffect(() => {
    const handleScroll = () => {
      if (heroSearchRef.current) {
        const heroSearchRect = heroSearchRef.current.getBoundingClientRect();
        const headerHeight = 100; // Account for header height
        const shouldStick = heroSearchRect.bottom <= headerHeight; // Stick immediately when hero search goes behind header

        // Update sticky state immediately
        setIsSearchSticky(shouldStick);

        // If sticky search just appeared, set up auto-focus after a short delay
        if (shouldStick && isScrollingToProducts) {
          // Clear any existing timeout
          if (scrollStopTimeoutRef.current) {
            clearTimeout(scrollStopTimeoutRef.current);
          }

          // Auto-focus after sticky search appears and scroll settles
          scrollStopTimeoutRef.current = setTimeout(() => {
            if (stickySearchInputRef.current) {
              console.log("Auto-focusing search input");
              stickySearchInputRef.current.focus();
            }
          }, 300); // Give time for sticky search to fully appear
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollStopTimeoutRef.current) {
        clearTimeout(scrollStopTimeoutRef.current);
      }
    };
  }, [isScrollingToProducts, isSearchSticky]);

  // Smooth scroll to category section
  const scrollToCategories = useCallback(() => {
    const controlsElement = document.querySelector(".luxury-shop__controls");
    if (controlsElement) {
      setIsScrollingToProducts(true);

      const headerHeight = 100; // Header height
      const stickySearchHeight = 80; // Sticky search height
      const targetPosition =
        controlsElement.getBoundingClientRect().top +
        window.pageYOffset -
        headerHeight -
        stickySearchHeight -
        20; // 20px extra padding

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      // Add temporary highlight effect
      controlsElement.classList.add("luxury-shop__controls--highlighted");

      // Clear scrolling flag and highlight after animation completes
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrollingToProducts(false);
        controlsElement.classList.remove("luxury-shop__controls--highlighted");
      }, 2000); // Keep highlight for 2 seconds
    }
  }, []);

  // Handle search focus with scroll animation
  const handleSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
    if (!isSearchSticky) {
      scrollToCategories();
    }
  }, [isSearchSticky, scrollToCategories]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
  };

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const getCategoryCount = (categorySlug: string) => {
    if (categorySlug === "all") return products.length;
    return products.filter(
      (product) => product.productCategory?.slug?.current === categorySlug
    ).length;
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollStopTimeoutRef.current) {
        clearTimeout(scrollStopTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Head>
        <title>Shop - Chethi&apos;s</title>
      </Head>
      <NextSeo {...pageSeo.shop} />

      <div className="luxury-shop">
        {/* Sticky Search Bar */}
        <AnimatePresence>
          {isSearchSticky && (
            <motion.div
              ref={stickySearchRef}
              className="luxury-shop__sticky-search"
              initial={{ y: -100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -100, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="luxury-shop__sticky-search-content">
                <div className="luxury-search-container luxury-search-container--sticky">
                  <SearchOutlined className="luxury-search-icon" />
                  <Input
                    ref={stickySearchInputRef}
                    placeholder="Search products, categories, or styles..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    className="luxury-search-input"
                    size="large"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="luxury-search-clear"
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                {searchQuery && (
                  <motion.div
                    className="luxury-shop__sticky-results-info"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    {filteredProducts.length} result
                    {filteredProducts.length !== 1 ? "s" : ""}
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Compact Hero Section with Search */}
        <motion.div
          className="luxury-shop__hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="luxury-shop__hero-content">
            <motion.div
              className="luxury-shop__header"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <h1 className="luxury-shop__title">Luxury Collection</h1>
              <p className="luxury-shop__subtitle">
                Discover our curated selection of sustainable luxury fashion
              </p>
            </motion.div>

            {/* Hero Search Bar */}
            <motion.div
              ref={heroSearchRef}
              className={`luxury-shop__search ${
                isSearchFocused ? "luxury-shop__search--focused" : ""
              }`}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="luxury-search-container">
                <SearchOutlined className="luxury-search-icon" />
                <Input
                  placeholder="Search products, categories, or styles..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={handleSearchFocus}
                  onBlur={() => setIsSearchFocused(false)}
                  className="luxury-search-input"
                  size="large"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="luxury-search-clear"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Subtle Decorative Elements */}
          <div className="luxury-shop__hero-decoration">
            <div className="luxury-shop__hero-pattern"></div>
          </div>
        </motion.div>

        <div className="luxury-shop__container">
          {/* Enhanced Filters & Sort */}
          <motion.div
            className="luxury-shop__controls"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="luxury-shop__filters">
              <div className="luxury-shop__filters-header">
                <FilterOutlined className="luxury-shop__filters-icon" />
                <span className="luxury-shop__filters-title">Categories</span>
              </div>
              <div className="luxury-shop__filters-buttons">
                <motion.button
                  className={`luxury-filter-btn ${
                    activeCategory === "all" ? "luxury-filter-btn--active" : ""
                  }`}
                  onClick={() => handleCategoryChange("all")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="luxury-filter-btn__label">All Products</span>
                  <span className="luxury-filter-btn__count">
                    ({getCategoryCount("all")})
                  </span>
                </motion.button>
                {categories.map((category) => (
                  <motion.button
                    key={category.slug.current}
                    className={`luxury-filter-btn ${
                      activeCategory === category.slug.current
                        ? "luxury-filter-btn--active"
                        : ""
                    }`}
                    onClick={() => handleCategoryChange(category.slug.current)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="luxury-filter-btn__label">
                      {category.title}
                    </span>
                    <span className="luxury-filter-btn__count">
                      ({getCategoryCount(category.slug.current)})
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>

            <div className="luxury-shop__sort">
              <div className="luxury-shop__sort-header">
                <SortAscendingOutlined className="luxury-shop__sort-icon" />
                <span className="luxury-shop__sort-title">Sort By</span>
              </div>
              <select
                value={sortBy}
                onChange={(e) => handleSortChange(e.target.value)}
                className="luxury-sort-select"
              >
                <option value="newest">Newest Arrivals</option>
                <option value="name">Alphabetical</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </motion.div>

          {/* Compact Results Summary */}
          <motion.div
            className="luxury-shop__results-summary"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.9 }}
          >
            <div className="luxury-shop__results-content">
              <p className="luxury-shop__results-text">
                {searchQuery ? (
                  <>
                    <strong>{filteredProducts.length}</strong> result
                    {filteredProducts.length !== 1 ? "s" : ""}
                    {activeCategory !== "all" && (
                      <>
                        {" "}
                        in{" "}
                        <em>
                          {
                            categories.find(
                              (c) => c.slug.current === activeCategory
                            )?.title
                          }
                        </em>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <strong>{filteredProducts.length}</strong> product
                    {filteredProducts.length !== 1 ? "s" : ""}
                    {activeCategory !== "all" && (
                      <>
                        {" "}
                        in{" "}
                        <em>
                          {
                            categories.find(
                              (c) => c.slug.current === activeCategory
                            )?.title
                          }
                        </em>
                      </>
                    )}
                  </>
                )}
              </p>
              {searchQuery && (
                <motion.div
                  className="luxury-shop__search-indicator"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  Searching for &ldquo;{searchQuery}&rdquo;
                </motion.div>
              )}
            </div>
          </motion.div>

          {/* Luxury Products Grid */}
          <motion.div
            ref={productsRef}
            className="luxury-shop__products"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          >
            {filteredProducts.length > 0 ? (
              <div className="luxury-products-grid">
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product._id}
                    className="luxury-product-item"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <ProductItem product={product} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                className="luxury-shop__no-results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="luxury-shop__no-results-icon">
                  <SearchOutlined />
                </div>
                <h3 className="luxury-shop__no-results-title">
                  No products found
                </h3>
                <p className="luxury-shop__no-results-text">
                  {searchQuery ? (
                    <>
                      Try adjusting your search terms or{" "}
                      <button
                        onClick={clearSearch}
                        className="luxury-shop__clear-search"
                      >
                        clear search
                      </button>
                    </>
                  ) : (
                    "No products available in this category"
                  )}
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  try {
    const [products, categories] = await Promise.all([
      sanityFetch(productsQuery),
      sanityFetch(categoriesQuery),
    ]);

    return {
      props: {
        products: products || [],
        categories: categories || [],
        selectedCategory: query.category || null,
      },
    };
  } catch (error) {
    console.error("Error fetching shop data:", error);
    return {
      props: {
        products: [],
        categories: [],
        selectedCategory: null,
      },
    };
  }
};
